/**
 * Frontend mirror of the backend operator registry.
 *
 * For each field type, declares:
 *   - the operators we offer
 *   - the human label for the operator
 *   - the value input shape: 'none' | 'text' | 'number' | 'date' | 'date_range' |
 *                            'int' | 'int_list' | 'boolean' | 'picklist' | 'picklist_multi'
 *
 * Keep in sync with services/filters/operatorRegistry.js on the backend.
 */

export const OPERATORS_BY_TYPE = {
	text: [
		{ op: "equals",          label: "Equals",                input: "text" },
		{ op: "not_equals",      label: "Does Not Equal",        input: "text" },
		{ op: "contains",        label: "Contains",              input: "text" },
		{ op: "not_contains",    label: "Does Not Contain",      input: "text" },
		{ op: "begins_with",     label: "Begins With",           input: "text" },
		{ op: "not_begins_with", label: "Does Not Begin With",   input: "text" },
		{ op: "ends_with",       label: "Ends With",             input: "text" },
		{ op: "not_ends_with",   label: "Does Not End With",     input: "text" },
		{ op: "is_empty",        label: "Contains No Data",      input: "none" },
		{ op: "is_not_empty",    label: "Contains Data",         input: "none" },
	],

	number: [
		{ op: "equals",          label: "Equals",                       input: "number" },
		{ op: "not_equals",      label: "Does Not Equal",               input: "number" },
		{ op: "gt",              label: "Greater Than",                 input: "number" },
		{ op: "lt",              label: "Less Than",                    input: "number" },
		{ op: "gte",             label: "Greater Than or Equal To",     input: "number" },
		{ op: "lte",             label: "Less Than or Equal To",        input: "number" },
		{ op: "between",         label: "Between",                      input: "number_range" },
		{ op: "is_empty",        label: "Contains No Data",             input: "none" },
		{ op: "is_not_empty",    label: "Contains Data",                input: "none" },
	],

	date: [
		{ op: "on",                 label: "On",                   input: "date" },
		{ op: "on_or_after",        label: "On or After",          input: "date" },
		{ op: "on_or_before",       label: "On or Before",         input: "date" },
		{ op: "before",             label: "Before",               input: "date" },
		{ op: "after",              label: "After",                input: "date" },
		{ op: "between",            label: "Between",              input: "date_range" },
		{ op: "today",              label: "Today",                input: "none" },
		{ op: "yesterday",          label: "Yesterday",            input: "none" },
		{ op: "tomorrow",           label: "Tomorrow",             input: "none" },
		{ op: "this_week",          label: "This Week",            input: "none" },
		{ op: "last_week",          label: "Last Week",            input: "none" },
		{ op: "next_week",          label: "Next Week",            input: "none" },
		{ op: "this_month",         label: "This Month",           input: "none" },
		{ op: "last_month",         label: "Last Month",           input: "none" },
		{ op: "next_month",         label: "Next Month",           input: "none" },
		{ op: "this_year",          label: "This Year",            input: "none" },
		{ op: "last_year",          label: "Last Year",            input: "none" },
		{ op: "next_year",          label: "Next Year",            input: "none" },
		{ op: "last_x_days",        label: "Last X Days",          input: "int" },
		{ op: "next_x_days",        label: "Next X Days",          input: "int" },
		{ op: "older_than_x_days",  label: "Older Than X Days",    input: "int" },
		{ op: "is_empty",           label: "Contains No Data",     input: "none" },
		{ op: "is_not_empty",       label: "Contains Data",        input: "none" },
	],

	picklist: [
		{ op: "equals",          label: "Equals",                input: "picklist" },
		{ op: "not_equals",      label: "Does Not Equal",        input: "picklist" },
		{ op: "in",              label: "Equals (any of)",       input: "picklist_multi" },
		{ op: "not_in",          label: "Not in",                input: "picklist_multi" },
		{ op: "is_empty",        label: "Contains No Data",      input: "none" },
		{ op: "is_not_empty",    label: "Contains Data",         input: "none" },
	],

	boolean: [
		{ op: "equals",          label: "Equals",                input: "boolean" },
	],
};

export function operatorsForType(type) {
	return OPERATORS_BY_TYPE[type] || [];
}

export function operatorDef(type, op) {
	const list = OPERATORS_BY_TYPE[type];
	if (!list) return null;
	return list.find(o => o.op === op) || null;
}

export function operatorLabel(type, op) {
	const d = operatorDef(type, op);
	return d ? d.label : op;
}

/**
 * Given a (type, op) returns a "starter" value appropriate for the value editor.
 * Used when the user switches operators in the popover.
 */
export function defaultValueFor(type, op) {
	const d = operatorDef(type, op);
	if (!d) return null;
	switch (d.input) {
		case "none":            return null;
		case "text":            return "";
		case "number":          return "";
		case "int":             return "";
		case "date":            return "";
		case "date_range":      return ["", ""];
		case "number_range":    return ["", ""];
		case "picklist":        return null;
		case "picklist_multi":  return [];
		case "boolean":         return true;
		default:                return null;
	}
}
